import { Injectable } from '@angular/core';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import * as pdfmake from 'pdfmake/build/pdfmake';
import { comumService } from './comum.service';
import { LocalStorageService } from './localstorage/localstorage.service';

export interface ApiResponse<T> {
  meta?: {
    codigo?: number;
    mensagem?: string;
  };
  response: T;
}

export interface PreRegistroResponse {
  codigo_verificador: string;
}

export interface FinalizacaoResponse {
  codigo_verificador?: string;
  codigo_crc?: string;
  mensagem?: string;
}

export interface EmitirPdfAutenticadoParams {
  pdfDefinition: unknown;
  fileName: string;
  emitente: string;
  perfil: string;
}

export interface EmitirPdfAutenticadoResult {
  codigo_verificador: string;
  pdfBlob: Blob;
  finalizacao?: FinalizacaoResponse;
}

interface ValidarPdfPayload {
  codigo_verificador: string;
  pdf: Blob;
}


@Injectable({
  providedIn: 'root'
})
export class ConferenciaAutenticidadeService {

  constructor(
    private service: comumService,
    private localStorage: LocalStorageService
  ) {}

  async emitirPdfAutenticado(
    params: EmitirPdfAutenticadoParams
  ): Promise<EmitirPdfAutenticadoResult> {
    const preRegistro = await this.preRegistrarEmissao(
      params.emitente,
      params.perfil
    );

    const codigoVerificador = preRegistro.codigo_verificador;

    const pdfBlob = await this.gerarBlobPdf(params.pdfDefinition);

    const finalizacao = await this.finalizarEmissao(
      pdfBlob,
      codigoVerificador,
      params.fileName
    );

    this.downloadBlob(pdfBlob, params.fileName);

    return {
      codigo_verificador: codigoVerificador,
      pdfBlob,
      finalizacao
    };
  }

  preRegistrarEmissao(
    emitente: string,
    perfil: string
  ): Promise<PreRegistroResponse> {
    const params = {
      token: this.localStorage.get('token'),
      emitente,
      perfil
    };

    return new Promise<PreRegistroResponse>((resolve, reject) => {
      this.service
        .post<ApiResponse<PreRegistroResponse>>(params, '/conferencia/pre-registrar')
        .subscribe({
          next: (rs: ApiResponse<PreRegistroResponse>) => {
            if (rs?.meta?.codigo === 200 && rs.response?.codigo_verificador) {
              resolve(rs.response);
              return;
            }

            reject(rs);
          },
          error: reject
        });
    });
  }

  finalizarEmissao(
    pdfBlob: Blob,
    codigoVerificador: string,
    fileName: string
  ): Promise<FinalizacaoResponse> {
    const formData = new FormData();

    formData.append('token', this.localStorage.get('token'));
    formData.append('codigo_verificador', codigoVerificador);
    formData.append('file', pdfBlob, fileName);

    return new Promise<FinalizacaoResponse>((resolve, reject) => {
      this.service
        .post<ApiResponse<FinalizacaoResponse>>(formData, '/conferencia/finalizar')
        .subscribe({
          next: (rs: ApiResponse<FinalizacaoResponse>) => {
            if (rs?.meta?.codigo === 200) {
              resolve(rs.response);
              return;
            }

            reject(rs);
          },
          error: reject
        });
    });
  }

  validarPdfPublico(
    file: File,
    codigoVerificador: string,
    captchaToken: string,
    captchaResposta: string
  ): Promise<unknown> {
    const formData = new FormData();

    formData.append('file', file, file.name);
    formData.append('codigo_verificador', codigoVerificador);
    formData.append('captcha_token', captchaToken);
    formData.append('g-recaptcha-response', captchaResposta);

    return new Promise((resolve, reject) => {
      this.service
        .post(formData, '/conferencia/validar', true)
        .subscribe({
          next: (rs: any) => {
            if (rs?.meta?.codigo === 200) {
              resolve(rs.response);
              return;
            }

            reject(rs);
          },
          error: reject
        });
    });
  }

  gerarCaptcha(): Promise<{ token: string; desafio: string }> {
    return new Promise((resolve, reject) => {
      this.service
        .get({ service: '/conferencia/captcha' })
        .subscribe({
          next: (rs: any) => {
            if (rs?.meta?.codigo === 200 && rs.response?.token) {
              resolve(rs.response as { token: string; desafio: string });
              return;
            }

            reject(rs);
          },
          error: reject
        });
    });
  }

  validarPdf(payload: ValidarPdfPayload) {
    const formData = new FormData();
  
    formData.append('codigo_verificador', payload.codigo_verificador);
    formData.append('file', payload.pdf, 'documento-validacao.pdf');
  
    return this.service.post(formData, '/conferencia/validar', true);
  }

  montarUrlValidacao(codigoVerificador: string): string {
    return (
      'http://' +
      window.location.host +
      '/#/validacao-autenticidade?codigo_verificador=' +
      codigoVerificador
    );
  }

  gerarBlobPdf(pdfDefinition: unknown): Promise<Blob> {
    return new Promise<Blob>((resolve) => {
      pdfmake
        .createPdf(pdfDefinition as TDocumentDefinitions)
        .getBlob((blob: Blob) => {
          resolve(blob);
        });
    });
  }

  downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(url);
  }
}